import { capitalize, get } from 'es-toolkit/compat';
import type { TFunction } from 'i18next';
import { type CarDataFields, type ConfigObject, DebugLevel, InfoGridFuelCategory } from '../../_configs/types';
import sStrDatafields from '../../_json/sStrDatafields';
import type { ConfigInfoGridPrefixes } from '../../_tools/utils';
import type { Car } from '../../_types/Car';
import type { Modules } from '../../modules/_definition/types';
import { type MetaData } from '../../vars';
import { generateLink } from '../generateLink';
import checkUndefined from '../tools/checkUndefined';
import checkUndefinedNullOrEmpty from '../tools/checkUndefinedNullOrEmpty';

export default function getCarMeta(translate: TFunction, car: Car<false>, settings: ConfigObject, module: Modules, loading: boolean): MetaData {
    const meta: MetaData = { canonical: undefined, desc: undefined, image: undefined, imageAlt: undefined, tags: undefined, title: undefined, titleRaw: undefined, url: undefined };

    if (loading === true) {
        const newCarName = car?.name
            ?.replace(/([0-9])-([0-9])/, '$1.$2')
            ?.split('-')
            ?.map((t) => capitalize(t)?.replace('Vw', 'VW'))
            ?.join(' ');
        meta.title = `Lade ${newCarName} (${car?.id})...`;
        meta.titleRaw = `Lade ${newCarName} (${car?.id})...`;
        return meta;
    }

    meta.image = car?.bilder?.url?.[0];
    meta.imageAlt = `Fahrzeugbild ${car?.name}`;
    meta.url = generateLink({ car, module, pathOnly: false, settings, type: 'car' });
    meta.title = car?.name;
    meta.titleRaw = car?.name;

    const dataFields = sStrDatafields(translate, settings?.general?.language, settings?.style?.fontSize);
    const bigInfoGridArray: ConfigInfoGridPrefixes<CarDataFields>[] = !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.big?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory])
        ? settings?.fahrzeugdetail?.infoGrid?.big?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory]
        : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.big?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT])
          ? settings?.fahrzeugdetail?.infoGrid?.big?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT]
          : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.big?.default?.[car?.verbrauch?.fuelCategory])
            ? settings?.fahrzeugdetail?.infoGrid?.big?.default?.[car?.verbrauch?.fuelCategory]
            : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.big?.default?.[InfoGridFuelCategory.DEFAULT])
              ? settings?.fahrzeugdetail?.infoGrid?.big?.default?.[InfoGridFuelCategory.DEFAULT]
              : [];
    const smallInfoGridArray: ConfigInfoGridPrefixes<CarDataFields>[] = !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.small?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory])
        ? settings?.fahrzeugdetail?.infoGrid?.small?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory]
        : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.small?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT])
          ? settings?.fahrzeugdetail?.infoGrid?.small?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT]
          : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.small?.default?.[car?.verbrauch?.fuelCategory])
            ? settings?.fahrzeugdetail?.infoGrid?.small?.default?.[car?.verbrauch?.fuelCategory]
            : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.small?.default?.[InfoGridFuelCategory.DEFAULT])
              ? settings?.fahrzeugdetail?.infoGrid?.small?.default?.[InfoGridFuelCategory.DEFAULT]
              : [];
    const listInfoGridArray: ConfigInfoGridPrefixes<CarDataFields>[] = !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.list?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory])
        ? settings?.fahrzeugdetail?.infoGrid?.list?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory]
        : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.list?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT])
          ? settings?.fahrzeugdetail?.infoGrid?.list?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT]
          : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.list?.default?.[car?.verbrauch?.fuelCategory])
            ? settings?.fahrzeugdetail?.infoGrid?.list?.default?.[car?.verbrauch?.fuelCategory]
            : !checkUndefined(settings?.fahrzeugdetail?.infoGrid?.list?.default?.[InfoGridFuelCategory.DEFAULT])
              ? settings?.fahrzeugdetail?.infoGrid?.list?.default?.[InfoGridFuelCategory.DEFAULT]
              : [];

    const infoGridArray: ConfigInfoGridPrefixes<CarDataFields>[] = [...(bigInfoGridArray || []), ...(smallInfoGridArray || []), ...(listInfoGridArray || [])];
    const properties: [string, string][] = [
        [undefined, [car?.hersteller, car?.modell, car?.typ]?.filter((val) => !checkUndefinedNullOrEmpty(val))?.join(' ')],
        ...infoGridArray.reduce((prev, curr) => {
            const clearKey = curr?.replace('[G]', '')?.replace('[L]', '') as CarDataFields;
            const keyToShow = clearKey === 'siegel_garantie' ? (!checkUndefinedNullOrEmpty(car?.siegel) && car?.siegel !== 0 ? 'siegel' : 'garantie') : clearKey;
            const itemToShow = dataFields?.find((f) => f?.i === keyToShow);
            const rawResult = !checkUndefinedNullOrEmpty(itemToShow?.renderTextValue)
                ? itemToShow?.renderTextValue(car)
                : !checkUndefinedNullOrEmpty(itemToShow?.renderValue)
                  ? itemToShow?.renderValue(car)
                  : get(car, itemToShow?.i, undefined);
            const result = !checkUndefinedNullOrEmpty(rawResult) && !['(unbekannt)'].includes(rawResult.toString()) ? rawResult : '-';
            if (!checkUndefinedNullOrEmpty(result) && result !== '-') {
                return [...prev, [itemToShow?.t, result]];
            } else {
                return prev;
            }
        }, []),
    ];

    meta.desc = properties?.reduce((prev, curr) => [...prev, !checkUndefinedNullOrEmpty(curr[0]) ? `${curr[0]}: ${curr[1]}` : curr[1]], [])?.join(', ');
    meta.tags = properties?.reduce((prev, curr) => [...prev, curr[1]], [])?.join(', ');

    const checkMagicLink = location?.pathname?.match(/.*\/magicId\/.*/g);
    const checkOldEinzelansicht = location?.search?.replace('?', '')?.match(/.*_carId[0-9]+/g);
    if (!checkUndefinedNullOrEmpty(car) && (!checkUndefinedNullOrEmpty(checkMagicLink) || (!checkUndefinedNullOrEmpty(checkOldEinzelansicht) && !settings?.general?.useOldRouter))) {
        const rawCanonical = generateLink({ type: 'car', car, module, pathOnly: false, settings });
        meta.canonical = rawCanonical;
        if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - getCarMeta - Generated canonical meta tag`, rawCanonical);
        }
    }

    return meta;
}
